/* eslint-disable indent */
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/Controls/Input';
import { StatsCard } from '@/components/Custom/Card/StatsCard';
import { DocumentsIcon } from '@/components/SvgIcons/DocumentsIcon';
import { MixedBlocksIcon } from '@/components/SvgIcons/MixedBlocksIcon';
import { OfficeIcon } from '@/components/SvgIcons/OfficeIcon';
import { RegisteredUserList } from '../components/RegisteredUserList';
import { DashboardChartCard } from '../components/DashboardChartCard';
import { ActivityCardContainer } from '../components/ActivityCardContainer';
import { TitleSection } from '../components/TitleSection';
import { SubscribedGroupList } from '../components/SubscribedGroupList';
import {
  useGetGroupsEndingSubscriptions,
  useGetTotalActiveGroups,
} from '@/hooks/group.hooks';
import { useGetTotalActiveCompanies } from '@/hooks/company.hooks';
import { useGetTotalActiveBranches } from '@/hooks/branch.hooks';
import {
  useGetAllVehiculeChecksToModerate,
  useGetAllVehiculeChecksToValidate,
  useGetCreatedVehiculeChecksPerMonth,
  useGetTotalActiveVehiculeChecks,
} from '@/hooks/vehicule-check.hooks';
import {
  useGetCreatedUsersPerMonth,
  useGetLatestCreatedUsers,
} from '@/hooks/user.hooks';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import Loader from '@/components/Loading/Loader';
import { RegisteredUserCard } from '../components/RegisteredUserCard';
import { formatThousands } from '@/utils/string.utils';
import { DashboardHeader } from '@/components/Header/Page/DashboardHeader';

const HomePage = () => {
  const { t } = useTranslation('dashboard');

  const { isCompanyManager, isBranchManager } = useUserRole();
  const { limitedRoleLoggedInUserId } = useAuth();

  const { data: totalActiveGroups, isLoading: isLoadingTotalActiveGroups } =
    useGetTotalActiveGroups({
      userId: limitedRoleLoggedInUserId,
    });

  const {
    data: totalActiveCompanies,
    isLoading: isLoadingTotalActiveCompanies,
  } = useGetTotalActiveCompanies({
    userId: limitedRoleLoggedInUserId,
  });

  const { data: totalActiveBranches, isLoading: isLoadingTotalActiveBranches } =
    useGetTotalActiveBranches({
      userId: limitedRoleLoggedInUserId,
    });

  const {
    data: totalActiveVehiculeChecks,
    isLoading: isLoadingTotalActiveVehiculeChecks,
  } = useGetTotalActiveVehiculeChecks({
    userId: limitedRoleLoggedInUserId,
    onlyCompleted: true,
  });

  const { data: latestCreatedUsers, isLoading: isLoadingLatestCreatedUsers } =
    useGetLatestCreatedUsers({
      id: limitedRoleLoggedInUserId,
      numberOfItems: 2,
    });

  const {
    data: createdVehiculeChecksPerMonth,
    isLoading: isLoadingCreatedVehiculeChecksPerMonth,
  } = useGetCreatedVehiculeChecksPerMonth(limitedRoleLoggedInUserId);

  const {
    data: createdUsersPerMonth,
    isLoading: isLoadingCreatedUsersPerMonth,
  } = useGetCreatedUsersPerMonth(limitedRoleLoggedInUserId);

  const {
    data: vehiculeChecksToModerate,
    isLoading: isLoadingVehiculeChecksToModerate,
  } = useGetAllVehiculeChecksToModerate({ userId: limitedRoleLoggedInUserId });

  const {
    data: vehiculeChecksToValidate,
    isLoading: isLoadingVehiculeChecksToValidate,
  } = useGetAllVehiculeChecksToValidate({ userId: limitedRoleLoggedInUserId });

  const {
    data: groupsEndingSubscriptions,
    isLoading: isLoadingGroupsEndingSubscriptions,
  } = useGetGroupsEndingSubscriptions(limitedRoleLoggedInUserId);

  const isLoadingAll =
    isLoadingTotalActiveGroups ||
    isLoadingTotalActiveCompanies ||
    isLoadingTotalActiveBranches ||
    isLoadingTotalActiveVehiculeChecks ||
    isLoadingLatestCreatedUsers ||
    isLoadingCreatedVehiculeChecksPerMonth ||
    isLoadingCreatedUsersPerMonth ||
    isLoadingVehiculeChecksToModerate ||
    isLoadingVehiculeChecksToValidate ||
    isLoadingGroupsEndingSubscriptions;

  return (
    <div>
      <DashboardHeader title={t('common:dashboard')}>
        <Input
          placeholder={t('common:search')}
          type="text"
          name="search"
          autoComplete="off"
          leftIcon={
            <i className="ki-outline ki-magnifier text-lg text-gray-8990A8" />
          }
          inputWrapperClassName="w-[300px] max-sm:w-full"
          inputClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
          onChange={() => {}}
        />
      </DashboardHeader>
      <div className="container-fixed mt-5">
        <div className="flex flex-wrap items-center gap-4">
          <StatsCard
            linkTo="/groups"
            disabled={isCompanyManager || isBranchManager}
            title={t('common:groups')}
            number={
              isLoadingAll
                ? 0
                : Number(formatThousands(totalActiveGroups?.data))
            }
            icon={<MixedBlocksIcon className="h-5 w-5 [&>path]:fill-primary" />}
          />
          <StatsCard
            linkTo="/companies"
            disabled={isBranchManager}
            title={t('common:companies')}
            number={
              isLoadingAll
                ? 0
                : Number(formatThousands(totalActiveCompanies?.data))
            }
            icon={<OfficeIcon className="h-5 w-5 [&>path]:fill-primary" />}
          />
          <StatsCard
            linkTo="/branches"
            title={t('common:branches')}
            number={
              isLoadingAll
                ? 0
                : Number(formatThousands(totalActiveBranches?.data))
            }
            icon={
              <DocumentsIcon className="-mt-0.5 h-5.5 w-5.5 [&>path]:fill-primary" />
            }
          />
          <StatsCard
            linkTo="/vehicule-checks"
            title={t('common:inspections')}
            number={
              isLoadingAll
                ? 0
                : Number(formatThousands(totalActiveVehiculeChecks?.data))
            }
            icon={
              <i className="-mt-[5px] ki-outline ki-frame text-xl text-primary" />
            }
          />
        </div>
        <div className="mt-9 flex flex-wrap items-start gap-4">
          <div className="flex flex-1 flex-col max-sm:w-full">
            <TitleSection
              title={t('lastRegisteredUsers')}
              viewText={t('common:viewAll')}
              viewLinkTo="/users"
            />
            <div className="mt-2.25">
              {isLoadingAll ? (
                <RegisteredUserCard
                  containerClassName="select-none [-webkit-touch-callout:none] [-webkit-user-select:none] hover:cursor-default"
                  fullName={t('common:loading')}
                  companyName={t('common:loading')}
                  registrationDate={t('common:loading')}
                  email={t('common:loading')}
                  phone={t('common:loading')}
                />
              ) : (
                <RegisteredUserList data={latestCreatedUsers?.data || []} />
              )}
            </div>
            <div className="mt-4 hidden max-[1450px]:block">
              <ActivityCardContainer
                t={t}
                toModerateData={
                  isLoadingAll
                    ? 0
                    : Number(
                        formatThousands(vehiculeChecksToModerate?.data?.length),
                      )
                }
                toValidateData={
                  isLoadingAll
                    ? 0
                    : Number(formatThousands(vehiculeChecksToValidate?.data))
                }
              />
            </div>
            <div className="mt-4">
              {isLoadingAll ? (
                <DashboardChartCard
                  t={t}
                  createdVehiculeCheckData={[]}
                  createdUsersData={[]}
                />
              ) : (
                <DashboardChartCard
                  t={t}
                  createdVehiculeCheckData={createdVehiculeChecksPerMonth?.data}
                  createdUsersData={createdUsersPerMonth?.data}
                />
              )}
            </div>
          </div>
          <div className="max-[1450px]:w-full">
            <div className="max-[1450px]:hidden">
              <ActivityCardContainer
                t={t}
                toModerateData={
                  isLoadingAll ? 0 : vehiculeChecksToModerate?.data?.length || 0
                }
                toValidateData={
                  isLoadingAll ? 0 : vehiculeChecksToValidate?.data || 0
                }
              />
            </div>
            {!groupsEndingSubscriptions?.data?.length ? (
              <></>
            ) : (
              !isLoadingAll && (
                <>
                  <div className="mt-4.5">
                    <TitleSection
                      title={t('groupsNearEndOfSubscription')}
                      viewText={t('common:viewAll')}
                      viewLinkTo="/groups"
                    />
                  </div>
                  <div className="mt-2.25">
                    <SubscribedGroupList
                      t={t}
                      data={groupsEndingSubscriptions?.data}
                    />
                  </div>
                </>
              )
            )}
          </div>
        </div>
      </div>
      {isLoadingAll && <Loader />}
    </div>
  );
};

export default HomePage;
